import { ArrowPathIcon } from "@heroicons/react/24/outline";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { DOCX_ACCEPT } from "../../../constants/fileTypes";
import { toastMultilineErrors } from "../../../util";
import {
  createFileStepSchema,
  validateStep,
} from "../../../validation/abstractSchema";

interface UploadStepProps {
  formData: any;
  setFormData: (data: any) => void;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

const UploadStep: React.FC<UploadStepProps> = ({
  formData,
  setFormData,
  onBack,
  onSubmit,
  isSubmitting,
}) => {
  /***************************
   * State Variables
   ***************************/
  const [title, setTitle] = useState(formData.title || "");
  const [abstractFile, setAbstractFile] = useState<File | null>(
    formData.abstractFile || null,
  );
  const [titleCharCount, setTitleCharCount] = useState(0);
  const [showAllSymbolPopup, setShowAllSymbolPopup] = useState(false);
  const [activeFieldForSymbols, setActiveFieldForSymbols] = useState("");
  const [lastInsertedSymbol, setLastInsertedSymbol] = useState("");
  // State to track if special characters have been added
  const [symbolsAdded, setSymbolsAdded] = useState({
    title: false,
    content: false,
  });

  /***************************
   * Ref
   ***************************/
  // Refs for Quill editors
  const titleQuillRef = useRef<ReactQuill>(null);
  const contentQuillRef = useRef<ReactQuill>(null);

  /***************************
   * Constants
   ***************************/
  const MAX_TITLE_CHARS = 500;
  const specialSymbols = [
    { char: "α", name: "Alpha" },
    { char: "β", name: "Beta" },
    { char: "γ", name: "Gamma" },
    { char: "δ", name: "Delta" },
    { char: "°", name: "Degree" },
    { char: "±", name: "Plus-minus" },
    { char: "≥", name: "Greater than or equal" },
    { char: "≤", name: "Less than or equal" },
    { char: "≠", name: "Not equal" },
    { char: "≈", name: "Approximately equal" },
    { char: "∞", name: "Infinity" },
    { char: "µ", name: "Mu" },
    { char: "π", name: "Pi" },
    { char: "∑", name: "Summation" },
    { char: "∆", name: "Delta" },
  ];
  // Quill modules configuration - using standard toolbar without custom symbols
  const quillModules = {
    toolbar: [
      ["bold", "italic"],
      [{ script: "sub" }, { script: "super" }],
      ["clean"], // remove formatting button
    ],
    clipboard: {
      matchVisual: false,
    },
    keyboard: {
      bindings: {
        "list autofill": null, // Disables auto-numbered list on "1. "
        "bullet list": null, // Disables auto-bullet list on "- "
        "ordered list": null, // Additional safeguard for ordered lists
      },
    },
  };

  /***************************
   * Handlers
   ***************************/
  const updateTitleCharCount = (content: string) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    setTitleCharCount(textContent.length);
  };

  useEffect(() => {
    if (title) {
      updateTitleCharCount(title);
    }
  }, [title]);

  // Function to add a special symbols popup button to Quill toolbar
  const addSymbolPopupButton = (
    quill: any,
    symbols: any[],
    fieldId: string,
  ) => {
    // Use setTimeout to ensure the toolbar is fully rendered
    setTimeout(() => {
      const toolbar = quill.getModule("toolbar");
      if (toolbar) {
        const toolbarElement = toolbar.container;

        // Check if we already added the button to avoid duplicates
        if (toolbarElement.querySelector(".symbol-popup")) {
          return;
        }

        // Add a container for the special symbols button
        const symbolButtonContainer = document.createElement("span");
        symbolButtonContainer.className = "ql-formats symbol-popup";

        const button = document.createElement("button");
        button.type = "button";
        button.className = "ql-symbol-button";
        button.innerHTML = "Ω";
        button.title = "Special Symbols";
        button.style.fontFamily = "Times New Roman, serif";
        button.style.fontSize = "16px";
        button.style.padding = "4px 8px";
        button.style.margin = "0 1px";
        button.style.borderRadius = "4px";
        button.style.cursor = "pointer";
        button.style.fontWeight = "bold";
        button.style.display = "flex";
        button.style.alignItems = "center";
        button.style.justifyContent = "center";

        button.addEventListener("mouseenter", () => {
          button.style.background = "#f3f4f6";
          button.style.borderColor = "#9ca3af";
        });

        button.addEventListener("mouseleave", () => {
          button.style.background = "#f9fafb";
          button.style.borderColor = "#d1d5db";
        });

        button.addEventListener("click", () => {
          setActiveFieldForSymbols(fieldId);
          setShowAllSymbolPopup(true);
        });

        symbolButtonContainer.appendChild(button);

        // Append to toolbar
        toolbarElement.appendChild(symbolButtonContainer);
      }
    }, 100); // Small delay to ensure DOM is ready
  };

  // Insert special symbol from popup
  const insertSymbol = (char: string) => {
    let quillInstance = null;

    if (activeFieldForSymbols === "title") {
      quillInstance = titleQuillRef.current;
    } else if (activeFieldForSymbols === "content") {
      quillInstance = contentQuillRef.current;
    }

    if (quillInstance) {
      const quill = quillInstance.getEditor();

      if (quill) {
        const range = quill.getSelection();

        if (range) {
          quill.insertText(range.index, char, "user");
          quill.setSelection({ index: range.index + char.length, length: 0 });
        } else {
          const length = quill.getLength();
          const insertPosition = length > 0 ? length - 1 : 0;
          quill.insertText(insertPosition, char, "user");
          quill.setSelection({
            index: insertPosition + char.length,
            length: 0,
          });
        }

        quill.focus();

        setLastInsertedSymbol(char);
        setTimeout(() => setLastInsertedSymbol(""), 500);
      }
    }
  };

  const handleTitleChange = (
    content: any,
    _delta: any,
    _source: any,
    editor: any,
  ) => {
    const plainText = editor.getText();
    const charCount = plainText.length - 1;

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const textNodes = tempDiv.querySelectorAll("*");
    textNodes.forEach((node: Element) => {
      if (node.childNodes.length > 0) {
        node.childNodes.forEach((child: Node) => {
          if (child.nodeType === 3) {
            child.textContent = child.textContent?.toUpperCase() || "";
          }
        });
      }
    });
    const uppercasedContent = tempDiv.innerHTML;

    setTitle(uppercasedContent);
    setTitleCharCount(charCount);
    setFormData({ ...formData, title: uppercasedContent });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setAbstractFile(file);
    setFormData({ ...formData, abstractFile: file });
  };

  const handleRemoveFile = () => {
    setAbstractFile(null);
    setFormData({ ...formData, abstractFile: null });
    // Reset the file input
    const fileInput = document.querySelector(
      'input[type="file"]',
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleSubmit = () => {
    // Extract plain text from HTML title for validation
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = title;
    const plainTitle = tempDiv.textContent || tempDiv.innerText || "";

    // Only validate the fields that are part of the file upload schema
    const stepData = {
      title: plainTitle,
      abstractFile: abstractFile,
    };

    const validation = validateStep(stepData, createFileStepSchema());
    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors || {});
      toastMultilineErrors(errorMessages);
      return;
    }

    // Update formData with the HTML title (for submission)
    setFormData({ ...formData, title: title, abstractFile: abstractFile });
    onSubmit();
  };

  /***************************
   * Effects
   ***************************/
  // Effect to add special symbols popup button to title editor
  useEffect(() => {
    if (titleQuillRef.current && !symbolsAdded.title) {
      const quill = titleQuillRef.current.getEditor();
      addSymbolPopupButton(quill, specialSymbols, "title");

      const editorElement = quill.root;
      if (editorElement) {
        editorElement.style.textTransform = "uppercase";
      }

      setSymbolsAdded((prev) => ({ ...prev, title: true }));
    }
  }, [title, symbolsAdded.title]);

  useEffect(() => {
    if (title) {
      updateTitleCharCount(title);
    }
  }, [title]);

  return (
    <div className="relative w-full bg-white">
      <div className="flex flex-col items-start w-full space-y-10">
        {/* Upload Abstract Section */}
        <div className="flex flex-col w-full space-y-2">
          <p className="text-lg font-bold leading-8 text-gray-900">
            Upload Abstract
          </p>
          <p className="text-md font-normal leading-7 text-gray-900">
            Download the abstract template and upload your completed file to
            proceed with your submission.
          </p>
        </div>

        {/* Template Download Section */}
        <div className="flex flex-col w-full space-y-4">
          <h3 className="text-md font-semibold text-gray-900">
            1. Download Abstract Template
          </h3>
          <div className="flex space-x-4">
            <a
              href="/templates/msn_2026_case_report_template.docx"
              download="MSN_2026_Case_Report_Template.docx"
              className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Case Report Template
            </a>
            <a
              href="/templates/msn_2026_research_template.docx"
              download="MSN_2026_Research_Template.docx"
              className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Research Template
            </a>
          </div>
        </div>

        {/* Abstract Title Section */}
        <div className="flex flex-col w-full space-y-4">
          <h3 className="text-md font-semibold text-gray-900">
            2. Abstract Title
          </h3>
          <div className="border border-gray-300" data-editor="title">
            <ReactQuill
              ref={titleQuillRef}
              theme="snow"
              value={title}
              onChange={handleTitleChange}
              modules={quillModules}
              placeholder=""
            />
          </div>
          <div
            className={`text-right text-sm mt-1 ${
              titleCharCount > MAX_TITLE_CHARS
                ? "text-red-500 font-semibold"
                : "text-gray-500"
            }`}
          >
            Character: {titleCharCount}/{MAX_TITLE_CHARS}
          </div>
        </div>

        {/* Upload Abstract Section */}
        <div className="flex flex-col w-full space-y-4">
          <h3 className="text-md font-semibold text-gray-900">
            3. Upload Abstract
          </h3>
          <p className="text-sm text-gray-600">
            You may upload files up to <span className="font-medium">10MB</span>
            .
          </p>

          <div className="flex flex-col space-y-2">
            <input
              id="abstract-file-upload"
              type="file"
              accept={DOCX_ACCEPT}
              onChange={handleFileChange}
              className="hidden"
            />
            <label
              htmlFor="abstract-file-upload"
              className="w-fit inline-block px-4 py-2 bg-blue-900 text-white rounded cursor-pointer hover:bg-blue-800"
            >
              Choose File
            </label>
            {abstractFile && (
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <span>{abstractFile.name}</span>
                <button
                  type="button"
                  onClick={handleRemoveFile}
                  className="text-red-500 hover:text-red-700 ml-2"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Special Symbol Popup */}
        {showAllSymbolPopup && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ backgroundColor: "rgba(0, 0, 0, 0.1)" }}
          >
            <div className="bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">
                  Select Special Symbol
                  {lastInsertedSymbol && (
                    <span className="ml-3 text-sm text-green-600 animate-pulse">
                      ✓ Inserted: {lastInsertedSymbol}
                    </span>
                  )}
                </h3>
                <button
                  onClick={() => setShowAllSymbolPopup(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-8 gap-2 mb-4 sm:grid-cols-10">
                {specialSymbols.map((symbol, index) => (
                  <button
                    key={`${symbol.char}-${index}`}
                    onClick={() => insertSymbol(symbol.char)}
                    className="p-2 text-lg border rounded hover:bg-blue-100 hover:border-blue-500 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                    title={`${symbol.name} (${symbol.char})`}
                    style={{ fontFamily: "Times New Roman, serif" }}
                  >
                    {symbol.char}
                  </button>
                ))}
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowAllSymbolPopup(false)}
                  className="px-4 py-2 text-white transition-colors bg-gray-500 rounded hover:bg-gray-600"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-between w-full">
          <button
            onClick={onBack}
            className="flex items-center px-6 py-2 transition-colors bg-blue-100 rounded cursor-pointer hover:bg-blue-200"
          >
            <div className="flex items-center space-x-2">
              <span className="text-sm font-normal leading-5 text-gray-900">
                ← Back
              </span>
            </div>
          </button>

          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center px-6 py-2 transition-colors bg-blue-900 rounded cursor-pointer hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed min-w-[100px]"
          >
            <div className="flex items-center justify-center space-x-2 w-full">
              {isSubmitting ? (
                <span className="py-0.5">
                  <ArrowPathIcon className="h-4 w-4 animate-spin text-white" />
                </span>
              ) : (
                <span className="text-sm font-normal leading-5 text-white">
                  Submit
                </span>
              )}
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadStep;
